import { Select, InputNumber } from "antd";
import { ModalOptionLabel } from "./components";
import type {
  ColumnOption,
  MetricAggregation,
  MetricDisplayStyle,
} from "@/components/VivoPlots/types";

const AGGREGATION_OPTIONS: {
  label: string;
  value: MetricAggregation;
  needsColumn: boolean;
}[] = [
  { label: "Count rows", value: "count", needsColumn: false },
  {
    label: "Count distinct values",
    value: "count-distinct",
    needsColumn: true,
  },
  { label: "Sum of column", value: "sum", needsColumn: true },
  { label: "Mean of column", value: "mean", needsColumn: true },
  { label: "Minimum of column", value: "min", needsColumn: true },
  { label: "Maximum of column", value: "max", needsColumn: true },
];

const DISPLAY_STYLES: { label: string; value: MetricDisplayStyle }[] = [
  { label: "Number", value: "number" },
  { label: "Donut", value: "donut" },
];

const DENOMINATOR_SOURCES = [
  { label: "All rows (total)", value: "total" },
  { label: "Fixed number", value: "fixed" },
  { label: "Custom filter", value: "filter" },
];

const PERIOD_PRESET_OPTIONS = [7, 14, 30, 60, 90, 180, 365].map((n) => ({
  label:
    n >= 365
      ? "1 year"
      : n >= 30
        ? `${Math.round(n / 30)} month${n >= 60 ? "s" : ""}`
        : `${n} days`,
  value: n,
}));

interface MetricCardConfigTabProps {
  setters: any;
  getters: any;
  columnOptions: ColumnOption[];
  columnsTitleCreator: (column: string) => string;
}

export const MetricCardConfigTab = ({
  setters,
  getters,
  columnOptions,
  columnsTitleCreator,
}: MetricCardConfigTabProps) => {
  const {
    metricDisplayStyle,
    metricAggregation,
    metricValueColumn,
    metricDenominatorSource,
    metricDenominatorFixed,
    metricTrendDateColumn,
    metricTrendPeriods,
    metricTrendDefaultPeriod,
  } = getters;

  const {
    setMetricDisplayStyle,
    setMetricAggregation,
    setMetricValueColumn,
    setMetricDenominatorSource,
    setMetricDenominatorFixed,
    setMetricTrendDateColumn,
    setMetricTrendPeriods,
    setMetricTrendDefaultPeriod,
  } = setters;

  const numericColumns = columnOptions.filter((col) => {
    const dt = col.data_type;
    return !dt || dt === "numeric";
  });

  // count and count-distinct work on any column; numeric aggregations need a numeric column
  const valueColumnOptions =
    !metricAggregation ||
    metricAggregation === "count" ||
    metricAggregation === "count-distinct"
      ? columnOptions
      : numericColumns;

  const dateColumns = columnOptions.filter((col) => {
    const dt = col.data_type;
    return !dt || dt === "datetime" || dt === "date";
  });

  return (
    <div className="flex flex-col gap-4 pr-2 pb-8">
      {/* Display style */}
      <div className="flex flex-col gap-2">
        <ModalOptionLabel
          label="Display style"
          tooltip="How the metric value is presented on the dashboard"
        />
        <div className="grid grid-cols-3 gap-2">
          {DISPLAY_STYLES.map((style) => (
            <button
              key={style.value}
              onClick={() => setMetricDisplayStyle(style.value)}
              className={`
                py-1.5 px-2 text-sm font-medium border rounded-md transition-colors
                ${
                  metricDisplayStyle === style.value
                    ? "plot-button-selected border-[#0C98D0] bg-[#D2EFF9] text-[#064C68]"
                    : "border-gray-200 text-[#2B4752] hover:bg-[#ECFAFF]"
                }
              `}
            >
              {style.label}
            </button>
          ))}
        </div>
      </div>

      {/* Column / field */}
      <div className="flex flex-col gap-2">
        <ModalOptionLabel label="Column" tooltip="The column to measure" />
        <Select
          data-testid="metric-config-value-column-select"
          placeholder="Select column…"
          className="w-full plotmodal"
          allowClear
          options={valueColumnOptions.map((col) => ({
            label: columnsTitleCreator(col.name),
            value: col.name,
          }))}
          value={metricValueColumn || undefined}
          onChange={(val) => setMetricValueColumn(val ?? undefined)}
        />
      </div>

      {/* Aggregation */}
      <div className="flex flex-col gap-2">
        <ModalOptionLabel label="Measure" />
        <Select
          data-testid="metric-config-aggregation-select"
          placeholder="Select aggregation…"
          className="w-full plotmodal"
          options={AGGREGATION_OPTIONS.map((o) => ({
            label: o.label,
            value: o.value,
          }))}
          value={metricAggregation || undefined}
          onChange={(val) => {
            setMetricAggregation(val);
          }}
        />
      </div>

      {/* ── Donut config ───────────────────────────────────────────────── */}
      {metricDisplayStyle === "donut" && (
        <>
          <div className="flex flex-col gap-2">
            <ModalOptionLabel
              label="Denominator"
              tooltip="What counts as the total — determines the % shown in the donut"
            />
            <Select
              data-testid="metric-config-denominator-source-select"
              className="w-full plotmodal"
              options={DENOMINATOR_SOURCES}
              value={metricDenominatorSource ?? "total"}
              onChange={(val) => setMetricDenominatorSource(val)}
            />
          </div>

          {metricDenominatorSource === "fixed" && (
            <div className="flex flex-col gap-2">
              <ModalOptionLabel label="Fixed denominator value" />
              <InputNumber
                className="w-full plotmodal"
                min={1}
                placeholder="e.g. 100"
                value={metricDenominatorFixed ?? undefined}
                onChange={(val) => setMetricDenominatorFixed(val ?? undefined)}
              />
            </div>
          )}

          {metricDenominatorSource === "filter" && (
            <div className="flex flex-col gap-2">
              <ModalOptionLabel
                label="Denominator filter"
                tooltip="Configure denominator rows via the Data tab — use plot filters to define the denominator population"
              />
              <p className="text-[12px] text-[#556C75] leading-snug">
                Switch to the <strong>Data</strong> tab to define which rows
                form the denominator using the filter controls there.
              </p>
            </div>
          )}
        </>
      )}

      {/* ── Number: delta / change config ─────────────────────────────── */}
      {metricDisplayStyle === "number" && (
        <>
          <div className="flex flex-col gap-2">
            <ModalOptionLabel
              label="Date column"
              tooltip="Optional — select a date column to show a period-over-period change indicator below the value"
            />
            <Select
              data-testid="metric-config-trend-date-column-select"
              placeholder="None (no change indicator)"
              className="w-full plotmodal"
              allowClear
              options={dateColumns.map((col) => ({
                label: columnsTitleCreator(col.name),
                value: col.name,
              }))}
              value={metricTrendDateColumn || undefined}
              onChange={(val) => setMetricTrendDateColumn(val ?? undefined)}
            />
          </div>

          {metricTrendDateColumn && (
            <>
              <div className="flex flex-col gap-2">
                <ModalOptionLabel
                  label="Available periods"
                  tooltip="Which comparison windows the user can pick on the card"
                />
                <Select
                  mode="multiple"
                  data-testid="metric-config-trend-periods-select"
                  className="w-full plotmodal"
                  options={PERIOD_PRESET_OPTIONS}
                  value={metricTrendPeriods ?? [14, 30, 60, 90]}
                  onChange={(val) => setMetricTrendPeriods(val)}
                />
              </div>

              <div className="flex flex-col gap-2">
                <ModalOptionLabel label="Default period" />
                <Select
                  data-testid="metric-config-default-period-select"
                  className="w-full plotmodal"
                  options={PERIOD_PRESET_OPTIONS.filter((o) =>
                    (metricTrendPeriods ?? [14, 30, 60, 90]).includes(o.value),
                  )}
                  value={metricTrendDefaultPeriod ?? 30}
                  onChange={(val) => setMetricTrendDefaultPeriod(val)}
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};
